/**
 * Routing Strategies panel (rendered inside the Digital Experience → Routing tab).
 * Owns the data-feed (Static/Dynamic) switch for the domain plus full CRUD over
 * its routing strategies. Receives the resolved `{ serviceId, domain }` scope.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { DataTable, type ColumnDef } from '@vnetwork/ui/data';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  IconButton,
  Input,
  Skeleton,
  Switch,
  RadioGroup,
  RadioGroupItem,
  Tooltip,
  TooltipProvider,
} from '@vnetwork/ui/primitives';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useWafStrategyProfiles,
  useWafStrategies,
  useWafZoneConfig,
  useUpdateWafStrategy,
  useDeleteWafStrategy,
  useUpdateWafZoneConfig,
  wafExtractError,
  wafStrategyLabel,
  type WafStrategy,
} from '@/api/waf';
import { trackFeatureAction } from '@/lib/analytics-events';

const muted = (v: React.ReactNode) => <span style={{ color: 'var(--vn-fg-muted)' }}>{v}</span>;

const DATA_FEED_ITEMS = [
  { value: 'static', label: 'Static' },
  { value: 'dynamic', label: 'Dynamic' },
];

/**
 * A synthetic, always-present "Default" baseline row — the routing every domain
 * falls back to when no custom strategy applies. It's not a real API record, so
 * it's `lock`ed (no edit/delete/toggle) and `activation: true`.
 */
const DEFAULT_ID = -1;
const DEFAULT_ROW: WafStrategy = {
  id: DEFAULT_ID,
  optimization_id: 0,
  profile_id: 0,
  priority: 0,
  name: 'Default',
  lock: true,
  activation: true,
  detail: {},
};
const isDefaultRow = (s: WafStrategy) => s.id === DEFAULT_ID;

/** Active toggle — PATCHes `{ activation }`; optimistic with revert-on-error. */
function ActivationCell({
  serviceId,
  domain,
  strategy,
}: {
  serviceId: string;
  domain: string;
  strategy: WafStrategy;
}) {
  const { show } = useToast();
  const [on, setOn] = useState(strategy.activation);
  const update = useUpdateWafStrategy(serviceId, {
    onSuccess: (_d, vars) => {
      show({ tone: 'success', title: vars.input.activation ? 'Strategy enabled' : 'Strategy disabled' });
      trackFeatureAction('waf', 'routing', vars.input.activation ? 'enable' : 'disable');
    },
    onError: (e) => { setOn(strategy.activation); show({ tone: 'danger', title: wafExtractError(e, 'Update failed') }); },
  });

  return (
    <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
      <Switch
        checked={on}
        aria-label="Active"
        disabled={strategy.lock || update.isPending}
        onCheckedChange={(v) => {
          setOn(v);
          update.mutate({ domain, id: strategy.id, input: { activation: v } });
        }}
      />
    </div>
  );
}

export function WafRoutingStrategiesPanel({ serviceId, domain }: { serviceId: string; domain: string }) {
  const { show } = useToast();
  const navigate = useNavigate();
  const profilesQ = useWafStrategyProfiles(serviceId);
  const zoneQ = useWafZoneConfig(serviceId, domain);
  const strategiesQ = useWafStrategies(serviceId, domain);

  const [deleting, setDeleting] = useState<WafStrategy | null>(null);

  const profiles = useMemo(() => profilesQ.data ?? [], [profilesQ.data]);
  const profileName = useMemo(() => {
    const m = new Map(profiles.map((p) => [p.id, p.name]));
    return (id: number) => wafStrategyLabel(id, m.get(id));
  }, [profiles]);

  const dq = `domain=${encodeURIComponent(domain)}`;
  const goNew = () => navigate(`/services/waf/${serviceId}/routing/new?${dq}`);
  const goEdit = (s: WafStrategy) => navigate(`/services/waf/${serviceId}/routing/edit?${dq}&id=${s.id}`);

  const updateFeed = useUpdateWafZoneConfig(serviceId, {
    onSuccess: (_d, vars) => {
      show({ tone: 'success', title: `Data feed set to ${vars.input.data_feed}` });
      trackFeatureAction('waf', 'routing', 'edit');
    },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Update failed') }),
  });

  // Zone settings (CNAME flatten + resolver TTL) — local draft synced from the
  // loaded config, saved together via a partial PATCH.
  const [flatten, setFlatten] = useState(false);
  const [ttl, setTtl] = useState('');
  useEffect(() => {
    if (zoneQ.data) {
      setFlatten(zoneQ.data.cname_flatten);
      setTtl(String(zoneQ.data.ttl));
    }
  }, [zoneQ.data]);
  const ttlNum = Number(ttl);
  const ttlValid = ttl.trim() !== '' && Number.isInteger(ttlNum) && ttlNum >= 0;
  const settingsDirty =
    !!zoneQ.data && (flatten !== zoneQ.data.cname_flatten || (ttlValid && ttlNum !== zoneQ.data.ttl));
  const saveSettings = useUpdateWafZoneConfig(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Settings saved' }); trackFeatureAction('waf', 'routing', 'edit'); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Update failed') }),
  });
  const del = useDeleteWafStrategy(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Strategy deleted' }); trackFeatureAction('waf', 'routing', 'delete'); setDeleting(null); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Delete failed') }),
  });

  // Always surface the baseline default row, even before the user creates any.
  const rows = [DEFAULT_ROW, ...(strategiesQ.data ?? [])];

  const columns = useMemo<ColumnDef<WafStrategy, unknown>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 500 }}>
            {row.original.name || muted('—')}
            {isDefaultRow(row.original) && (
              <Badge tone="info" appearance="subtle" size="sm">Baseline</Badge>
            )}
          </span>
        ),
      },
      {
        id: 'type',
        header: 'Type',
        enableSorting: false,
        cell: ({ row }) =>
          isDefaultRow(row.original) ? (
            muted('—')
          ) : (
            <Badge tone="neutral" appearance="subtle" size="sm">{profileName(row.original.profile_id)}</Badge>
          ),
      },
      {
        id: 'priority',
        header: 'Priority',
        enableSorting: false,
        cell: ({ row }) => (isDefaultRow(row.original) ? muted('—') : muted(row.original.priority)),
      },
      {
        id: 'active',
        header: 'Active',
        enableSorting: false,
        cell: ({ row }) => <ActivationCell serviceId={serviceId} domain={domain} strategy={row.original} />,
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', gap: 2 }}>
            <TooltipProvider>
              <Tooltip content="Edit" side="top">
                <IconButton
                  variant="ghost"
                  size="sm"
                  aria-label="Edit strategy"
                  onClick={() => goEdit(row.original)}
                  disabled={row.original.lock}
                >
                  <Pencil size={14} />
                </IconButton>
              </Tooltip>
              <Tooltip content="Delete" side="top">
                <IconButton
                  variant="ghost"
                  size="sm"
                  aria-label="Delete strategy"
                  onClick={() => setDeleting(row.original)}
                  disabled={row.original.lock}
                >
                  <Trash2 size={14} />
                </IconButton>
              </Tooltip>
            </TooltipProvider>
          </div>
        ),
      },
    ],
    [serviceId, domain, profileName],
  );

  const dataFeed = zoneQ.data?.data_feed ?? 'static';

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* Data-feed mode */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--vn-space-3)',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 500 }}>Data feed</span>
          {zoneQ.isPending ? (
            <Skeleton style={{ height: 32, width: 160 }} />
          ) : (
            <RadioGroup
              value={dataFeed}
              onValueChange={(v) => updateFeed.mutate({ domain, input: { data_feed: v } })}
              disabled={updateFeed.isPending}
              style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 'var(--vn-space-4)' }}
            >
              {DATA_FEED_ITEMS.map((it) => (
                <label
                  key={it.value}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--vn-font-size-md)', cursor: 'pointer' }}
                >
                  <RadioGroupItem value={it.value} /> {it.label}
                </label>
              ))}
            </RadioGroup>
          )}
          <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
            Static routes by the strategies below; Dynamic routes by live measurement.
          </span>
        </div>
        <Button startIcon={<Plus size={15} />} onClick={goNew} disabled={!profiles.length}>
          New strategy
        </Button>
      </div>

      {/* Zone settings: CNAME flatten + resolver TTL */}
      <Card>
        <CardHeader
          actions={
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                if (ttlValid) saveSettings.mutate({ domain, input: { cname_flatten: flatten, ttl: ttlNum } });
              }}
              disabled={!settingsDirty || !ttlValid || saveSettings.isPending}
            >
              {saveSettings.isPending ? 'Saving…' : 'Save'}
            </Button>
          }
        >
          <CardTitle>Zone settings</CardTitle>
        </CardHeader>
        <CardBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            {/* CNAME flatten */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--vn-space-4)' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                <span style={{ fontWeight: 500 }}>CNAME flatten</span>
                <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-sm)' }}>
                  Resolve the zone CNAME to its target records at the edge.
                </span>
              </div>
              {zoneQ.isPending ? (
                <Skeleton style={{ height: 24, width: 44 }} />
              ) : (
                <Switch
                  checked={flatten}
                  onCheckedChange={setFlatten}
                  disabled={saveSettings.isPending}
                  aria-label="CNAME flatten"
                />
              )}
            </div>

            {/* Resolver TTL */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--vn-space-4)' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                <span style={{ fontWeight: 500 }}>TTL</span>
                <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-sm)' }}>
                  DNS resolver cache lifetime, in seconds.
                </span>
              </div>
              {zoneQ.isPending ? (
                <Skeleton style={{ height: 36, width: 140 }} />
              ) : (
                <Input
                  type="number"
                  min={0}
                  value={ttl}
                  onChange={(e) => setTtl(e.target.value)}
                  invalid={ttl.trim() !== '' && !ttlValid}
                  disabled={saveSettings.isPending}
                  endSlot={<span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>sec.</span>}
                  style={{ width: 140 }}
                  aria-label="Resolver TTL in seconds"
                />
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Strategies */}
      {strategiesQ.isPending ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 48 }} />
          ))}
        </div>
      ) : strategiesQ.isError ? (
        <Alert
          tone="danger"
          title="Couldn't load routing strategies"
          actions={<button onClick={() => strategiesQ.refetch()}>Retry</button>}
        >
          The WAF service couldn't return this domain's routing strategies. Try again.
        </Alert>
      ) : (
        // The baseline "Default" row is always present, so there's no empty state.
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => String(r.id)}
          emptyState="No routing strategies."
        />
      )}

      {deleting && (
        <ConfirmDialog
          open
          onOpenChange={(o) => { if (!o) setDeleting(null); }}
          title="Delete strategy?"
          description={`Delete the strategy "${deleting.name}"? This cannot be undone.`}
          confirmLabel="Delete"
          destructive
          loading={del.isPending}
          onConfirm={() => del.mutate({ domain, id: deleting.id })}
        />
      )}
    </div>
  );
}

export default WafRoutingStrategiesPanel;
